/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
/** @jsx jsx */

import { css, jsx } from '@emotion/react'
import { isEqual } from 'lodash'
import { useEffect, useRef } from 'react'

import { useEuiTheme } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { User } from '@modules/cloud-api/v1/types'
import type { AsyncRequestState, FoundUser, StackDeployment } from '@modules/ui-types'
import { CuiTable } from '@modules/cui/Table'
import { CuiTimeAgo } from '@modules/cui/TimeAgo'
import type { CuiTableColumn } from '@modules/cui/Table'

import { getShieldUsers } from '@/lib/stackDeployments/selectors/metadata'

import DeleteTempUser from './DeleteTempUser'
import AddTempUser from './AddTempUser'

import type { Action } from '@/types/redux'
import type { FC } from 'react'

export type StateProps = {
  newTempShieldUser: FoundUser | null
  user: User | null
  setDeploymentResourceMetadataRequest: AsyncRequestState
}

export type DispatchProps = {
  resetSetDeploymentResourceMetadata: (deployment: StackDeployment) => void
  resetTempShieldUser: () => void
  updateMetadata: (params: {
    deployment: StackDeployment
    updater: (metadata: { [key: string]: any }) => { [key: string]: any }
    requestMeta: { [key: string]: any }
    then?: () => Action<string>
  }) => void
}

export type ConsumerProps = {
  deployment: StackDeployment
}

type Props = StateProps & DispatchProps & ConsumerProps

const TempShieldUsers: FC<Props> = ({
  deployment,
  updateMetadata,
  setDeploymentResourceMetadataRequest,
  resetSetDeploymentResourceMetadata,
  resetTempShieldUser,
  newTempShieldUser,
  user,
}) => {
  const { euiTheme } = useEuiTheme()
  const prevUsersRef = useRef<FoundUser[] | undefined>()

  const users = getShieldUsers({ deployment })

  // componentDidUpdate equivalent - check if users changed
  useEffect(() => {
    const prevUsers = prevUsersRef.current

    if (prevUsers && !isEqual(prevUsers, users) && !setDeploymentResourceMetadataRequest.error) {
      resetSetDeploymentResourceMetadata(deployment)
    }

    prevUsersRef.current = users
  }, [
    users,
    setDeploymentResourceMetadataRequest.error,
    resetSetDeploymentResourceMetadata,
    deployment,
  ])

  // componentWillUnmount equivalent
  useEffect(
    () => () => {
      resetTempShieldUser()
    },
    [resetTempShieldUser],
  )

  const renderDelete = (user: FoundUser) => (
    <DeleteTempUser
      deployment={deployment}
      user={user}
      updateMetadata={updateMetadata}
      setDeploymentResourceMetadataRequest={setDeploymentResourceMetadataRequest}
    />
  )

  const columns: Array<CuiTableColumn<FoundUser>> = [
    {
      label: <FormattedMessage id='cluster-manage-temp-shield-users.name' defaultMessage='Name' />,
      render: (foundUser) => foundUser.username,
      sortKey: `username`,
    },
    {
      label: (
        <FormattedMessage
          id='cluster-manage-temp-shield-users.expiration'
          defaultMessage='Expiration'
        />
      ),
      render: (row) =>
        row.valid_until ? (
          <CuiTimeAgo date={row.valid_until} longTime={true} />
        ) : (
          <FormattedMessage
            id='cluster-manage-temp-shield-users.expiration-never'
            defaultMessage='Never'
          />
        ),
      sortKey: `valid_until`,
    },
    {
      label: (
        <FormattedMessage id='cluster-manage-temp-shield-users.actions' defaultMessage='Actions' />
      ),
      render: renderDelete,
    },
  ]

  const tempShieldUsersTableStyle = css({
    marginRight: euiTheme.size.s,
    td: {
      paddingTop: euiTheme.size.xs,
      paddingBottom: euiTheme.size.xs,
    },
    '@media (max-width: 700px)': {
      thead: {
        display: 'none',
      },
      'table, tbody, tr, td': {
        display: 'block',
      },
      '.cloudTable-inlineLabel': {
        display: 'block',
      },
    },
  })

  return (
    <div css={tempShieldUsersTableStyle}>
      <CuiTable<FoundUser>
        fullWidth={false}
        rows={users}
        getRowId={(row) => row.username}
        columns={columns}
      />

      <AddTempUser
        deployment={deployment}
        user={user}
        newTempShieldUser={newTempShieldUser}
        updateMetadata={updateMetadata}
        setDeploymentResourceMetadataRequest={setDeploymentResourceMetadataRequest}
      />
    </div>
  )
}

export default TempShieldUsers
